/**
 * The channel itself: two devices talking directly, encrypted between them.
 *
 * The handshake rules live in signalling.js. This is the part that turns a
 * finished handshake into something the app can send requests over, and the
 * part that decides when to stop trying and use the ordinary http route
 * instead.
 *
 * Nothing here trusts the service in the middle. It is handed descriptions
 * and candidate addresses and never anything else, and what comes back is
 * fed to WebRTC, which will refuse it if it is not what it claims to be. The
 * encryption on the channel is WebRTC's own and is not optional: there is no
 * mode in which this sends anything in the clear.
 */

import { createHandshake, decodeSignal, encodeSignal } from "./signalling";

// Who may help set a connection up now lives in ice.js: the list is asked
// of the service rather than compiled in, because the relay password in it
// expires every ten minutes.

/** How long to try before falling back. A phone on a train needs longer. */
export const CONNECT_TIMEOUT_MS = 15000;

export class ChannelError extends Error {}

/**
 * Wraps a data channel so the rest of the app can treat it like a transport
 * rather than a WebRTC object.
 */
export function wrapChannel(channel) {
  return {
    get ready() {
      return channel.readyState === "open";
    },
    send(text) {
      if (channel.readyState !== "open") {
        throw new ChannelError("The channel is not open.");
      }
      channel.send(text);
    },
    close() {
      channel.close();
    },
    /** What to do with what the other side says. */
    onMessage(handler) {
      channel.onmessage = (event) => handler(event.data);
    },
    /**
     * When it stops. A Mac that goes to sleep looks exactly like a broken
     * app from the phone unless something says so out loud.
     */
    onClosed(handler) {
      channel.onclose = () => handler();
      channel.onerror = () => handler();
    },
  };
}

/**
 * Which way to reach the desktop, given what happened.
 *
 * Kept as a decision on its own so the rule is visible and testable, rather
 * than spread through a connection routine where nobody can see what it does
 * when a phone changes network halfway.
 */
export function chooseTransport({ channelOpen, httpReachable }) {
  if (channelOpen) return "channel";
  if (httpReachable) return "http";
  return "none";
}

/**
 * Runs one side of the handshake over a signalling socket.
 *
 * The socket, the peer connection and the clock are all handed in, so this
 * can be driven end to end in a test without a network, a browser or a
 * service.
 */
export async function connectPeer({
  role,
  room,
  socket,
  peerConnection,
  timeoutMs = CONNECT_TIMEOUT_MS,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
}) {
  const handshake = createHandshake(role);

  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeoutImpl(() => {
      if (settled) return;
      settled = true;
      // Not an error the user should see. It means "use the other road".
      reject(new ChannelError("No direct channel within the time allowed."));
    }, timeoutMs);

    const finish = (value, failed) => {
      if (settled) return;
      settled = true;
      clearTimeoutImpl(timer);
      failed ? reject(value) : resolve(value);
    };

    const send = (kind, payload) => socket.send(encodeSignal({ room, role, kind, payload }));

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) send("candidate", event.candidate);
    };

    peerConnection.ondatachannel = (event) => {
      // The guest opens the channel, so this is the host's side of it.
      event.channel.onopen = () => {
        send("ready", null);
        finish(wrapChannel(event.channel));
      };
    };

    socket.onmessage = async (raw) => {
      let message;
      try {
        message = decodeSignal(typeof raw === "string" ? raw : raw.data);
      } catch {
        return; // the service is not trusted; unreadable is simply ignored
      }
      const { action } = handshake.receive(message);

      try {
        await act(action, message);
      } catch (e) {
        // Until now this was an unhandled rejection inside an event handler:
        // the connection simply stopped progressing and both screens showed
        // a timeout, which is the least informative way to fail.
        finish(new ChannelError(`Could not use what the other device sent: ${e.message}`), true);
      }
    };

    const act = async (action, message) => {
      if (action === "answer") {
        await peerConnection.setRemoteDescription(message.payload);
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        handshake.answered();
        send("description", answer);
      } else if (action === "accept") {
        await peerConnection.setRemoteDescription(message.payload);
      } else if (action === "add-candidate") {
        try {
          await peerConnection.addIceCandidate(message.payload);
        } catch {
          // A candidate that arrives before the description it belongs to is
          // normal and not worth failing over.
        }
      } else if (action === "close") {
        finish(new ChannelError("The other side went away."), true);
      }
    };

    if (role === "guest") {
      // The guest arrives, so the guest asks. Opening the channel here rather
      // than waiting means the host has something to answer.
      const channel = peerConnection.createDataChannel("novamund");
      channel.onopen = () => finish(wrapChannel(channel));
      handshake.peerArrived();
      Promise.resolve()
        .then(() => peerConnection.createOffer())
        .then(async (offer) => {
          await peerConnection.setLocalDescription(offer);
          handshake.offered();
          send("description", offer);
        })
        .catch((e) => finish(new ChannelError(e.message), true));
    }
  });
}
